"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { computeMarketStats, CircuitStock, MarketStats, ScannerMarketRow } from "@/lib/marketStats";

type ActiveList = "upper" | "lower" | "high52" | "low52";
type MarketListRow = ScannerMarketRow & { rule?: CircuitStock["rule"] };

const formatNumber = (value: number, digits = 0) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });

const formatSigned = (value: number | null | undefined, digits = 2) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(digits)}`;
};

const formatSignedPct = (value: number | null | undefined, digits = 2) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(digits)}%`;
};

export default function MarketStatsDetailPage() {
  const router = useRouter();
  const [stats, setStats] = useState<MarketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeList, setActiveList] = useState<ActiveList>("upper");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/scanner/market?limit=2000&use_snapshot=1");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const rows: ScannerMarketRow[] = Array.isArray(json?.rows) ? json.rows : [];
        const latestDate = json?.latest_date ?? null;
        const snapshotStats = json?.stats ?? null;
        setStats(computeMarketStats(rows, latestDate, snapshotStats));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load market stats");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const listView = useMemo(() => {
    if (!stats) return null;
    if (activeList === "upper") {
      return {
        title: "Upper Circuit Stocks",
        emptyText: "No upper circuit names found for this session.",
        tone: "positive" as const,
        rows: stats.upperCircuitStocks as MarketListRow[],
        formatRule: (row: MarketListRow) => (row.rule?.type === "abs" ? "+1 PKR" : "10%"),
      };
    }
    if (activeList === "lower") {
      return {
        title: "Lower Circuit Stocks",
        emptyText: "No lower circuit names found for this session.",
        tone: "negative" as const,
        rows: stats.lowerCircuitStocks as MarketListRow[],
        formatRule: (row: MarketListRow) => (row.rule?.type === "abs" ? "-1 PKR" : "-10%"),
      };
    }
    if (activeList === "high52") {
      return {
        title: "52 Week High Stocks",
        emptyText: "No 52 week high names found for this session.",
        tone: "positive" as const,
        rows: stats.fiftyTwoWeekHighStocks as MarketListRow[],
        formatRule: () => "52W High",
      };
    }
    return {
      title: "52 Week Low Stocks",
      emptyText: "No 52 week low names found for this session.",
      tone: "negative" as const,
      rows: stats.fiftyTwoWeekLowStocks as MarketListRow[],
      formatRule: () => "52W Low",
    };
  }, [stats, activeList]);

  const tileClass = (baseTone: "primary" | "positive" | "negative" | "neutral", isActive?: boolean) =>
    `market-stats-tile market-stats-tile-button is-${baseTone} ${isActive ? "is-active" : ""}`.trim();

  return (
    <section className="strategy-detail-section home-section">
      <div className="strategy-detail-container home-shell">
        <div className="home-card market-stats-detail-card">
          <div className="market-stats-detail-header">
            <div>
              <h1 className="page-title">Market Stats Detail</h1>
              <p className="page-subtitle">
                Click Upper/Lower Circuit or 52 Week High/Low cards to switch the symbol list.
              </p>
            </div>
            <div className="market-stats-actions">
              {stats?.latestDate && <span className="market-stats-date">As of {stats.latestDate}</span>}
              <button
                type="button"
                className="ui-button ui-button-ghost"
                onClick={() => router.push("/market-overview")}
              >
                Back to overview
              </button>
            </div>
          </div>

          {loading && <div className="market-stats-loading">Loading market stats...</div>}
          {error && <div className="market-stats-error">Error: {error}</div>}

          {!loading && !error && stats && listView && (
            <>
              <div className="market-stats-grid">
                <button type="button" className={tileClass("positive", activeList === "upper")} onClick={() => setActiveList("upper")}>
                  <span>Upper Circuit</span>
                  <strong>{formatNumber(stats.upperCircuit)}</strong>
                </button>
                <button type="button" className={tileClass("negative", activeList === "lower")} onClick={() => setActiveList("lower")}>
                  <span>Lower Circuit</span>
                  <strong>{formatNumber(stats.lowerCircuit)}</strong>
                </button>
                <button type="button" className={tileClass("positive", activeList === "high52")} onClick={() => setActiveList("high52")}>
                  <span>52 Week High</span>
                  <strong>{formatNumber(stats.fiftyTwoWeekHigh)}</strong>
                </button>
                <button type="button" className={tileClass("negative", activeList === "low52")} onClick={() => setActiveList("low52")}>
                  <span>52 Week Low</span>
                  <strong>{formatNumber(stats.fiftyTwoWeekLow)}</strong>
                </button>
              </div>

              <div className="market-stats-detail-section">
                <h2>{listView.title}</h2>
                {listView.rows.length === 0 ? (
                  <div className="market-stats-empty">{listView.emptyText}</div>
                ) : (
                  <div className="market-stats-table">
                    <div className="market-stats-row market-stats-head">
                      <span>Symbol</span>
                      <span>Name</span>
                      <span>Sector</span>
                      <span>Close</span>
                      <span>Change</span>
                      <span>Change %</span>
                      <span>Volume</span>
                      <span>Rule</span>
                    </div>
                    {listView.rows.map((row, idx) => (
                      <div key={`${activeList}-${row.symbol}-${idx}`} className="market-stats-row">
                        <span>{row.symbol}</span>
                        <span>{row.name || "—"}</span>
                        <span>{row.sector || "—"}</span>
                        <span>{row.close != null ? formatNumber(row.close, 2) : "—"}</span>
                        <span className={listView.tone === "positive" ? "is-positive" : "is-negative"}>
                          {formatSigned(row.change, 2)}
                        </span>
                        <span className={listView.tone === "positive" ? "is-positive" : "is-negative"}>
                          {formatSignedPct(row.change_pct, 2)}
                        </span>
                        <span>{row.volume != null ? formatNumber(row.volume) : "—"}</span>
                        <span>{listView.formatRule(row)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
